/**
 * Admin Reconciliation Console — read-only surface of ledger drift.
 * Shows 3 sections: wallet vs ledger, gift-card stock, campaign redemptions.
 */
import { useEffect, useState } from "react";
import { Scale, Download, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { toast } from "sonner";

export default function AdminReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reconciliation");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/reconciliation.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cyannect-reconciliation.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reconciliation CSV downloaded.");
    } catch { toast.error("CSV export failed"); }
  };

  if (loading && !data) return <div className="p-8 text-sm text-slate-500" data-testid="recon-loading">Loading reconciliation…</div>;

  const sections = data?.sections || {};
  const totalDrift = data?.total_drift_rows || 0;

  return (
    <div className="p-6 space-y-6" data-testid="admin-recon-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><Scale className="w-3 h-3" /> Ops</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">Reconciliation Console</h1>
          <p className="text-sm text-slate-500 mt-1">Where the ledger, gift-card stock, and cashback engine disagree — fix before month-end.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                  data-testid="recon-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button onClick={downloadCsv} className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                  data-testid="recon-export-csv">
            <Download className="w-3 h-3" /> Export CSV
          </button>
        </div>
      </header>

      {/* Overall status */}
      <div className={`pf-card p-5 flex items-center gap-4 ${totalDrift === 0 ? "border-emerald-200/60 bg-emerald-50/60" : "border-amber-200/60 bg-amber-50/60"}`}
           data-testid="recon-status">
        <div className={`w-11 h-11 rounded-xl text-white flex items-center justify-center shrink-0 ${totalDrift === 0 ? "bg-emerald-500" : "bg-amber-500"}`}>
          {totalDrift === 0 ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-slate-900 text-lg" data-testid="recon-summary">
            {totalDrift === 0 ? "All ledgers reconcile cleanly." : `${totalDrift} drift row${totalDrift === 1 ? "" : "s"} across ${Object.keys(sections).length} sections.`}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">Recomputed live on refresh — no cached numbers.</div>
        </div>
      </div>

      {/* Section 1 — Wallet vs Ledger */}
      <ReconSection title="Wallet balance vs cashback ledger" testid="recon-section-wallet"
                     section={sections.wallet_vs_ledger}
                     columns={[
                       { key: "user_id", label: "User" },
                       { key: "wallet_balance", label: "Wallet balance", fmt: (v) => cad(v) },
                       { key: "ledger_expected", label: "Ledger expected", fmt: (v) => cad(v) },
                       { key: "credits", label: "Credits", fmt: (v) => cad(v) },
                       { key: "debits", label: "Withdrawals", fmt: (v) => cad(v) },
                       { key: "drift", label: "Drift", fmt: (v) => driftCell(v, "CA$") },
                     ]} />

      {/* Section 2 — Gift-card stock */}
      <ReconSection title="Gift-card inventory vs orders" testid="recon-section-gc"
                     section={sections.gift_card_stock}
                     columns={[
                       { key: "name", label: "Gift card" },
                       { key: "inventory_count", label: "Inventory" },
                       { key: "expected", label: "Expected" },
                       { key: "initial_stock", label: "Initial" },
                       { key: "reserved", label: "Reserved" },
                       { key: "redeemed", label: "Redeemed" },
                       { key: "drift", label: "Drift", fmt: (v) => driftCell(v, "") },
                     ]} />

      {/* Section 3 — Campaign redemptions */}
      <ReconSection title="Campaign redemptions vs claimed_coupons" testid="recon-section-campaigns"
                     section={sections.campaign_redemptions}
                     columns={[
                       { key: "name", label: "Campaign" },
                       { key: "recorded_redemptions", label: "Recorded" },
                       { key: "actual_redemptions", label: "Actual" },
                       { key: "drift", label: "Drift", fmt: (v) => driftCell(v, "") },
                     ]} />
    </div>
  );
}

function driftCell(v, prefix) {
  const sign = v > 0 ? "+" : "";
  const tone = v === 0 ? "text-slate-500" : v > 0 ? "text-rose-600" : "text-amber-700";
  return <span className={`font-mono font-semibold ${tone}`}>{sign}{prefix}{Math.abs(v).toFixed ? v.toFixed(2) : v}</span>;
}

function ReconSection({ title, testid, section, columns }) {
  const rows = section?.rows || [];
  return (
    <div className="pf-card overflow-hidden" data-testid={testid}>
      <div className="p-4 border-b border-white/60 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-slate-900 text-lg">{title}</h2>
          <p className="text-xs text-slate-500 mt-0.5">{section?.description}</p>
        </div>
        <span className={`pf-chip ${rows.length === 0 ? "pf-chip-mint" : "pf-chip-amber"}`}
               data-testid={`${testid}-count`}>
          {rows.length} drift{rows.length === 1 ? "" : "s"}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>
          <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-500 mb-2" />
          Clean — no drift detected.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-white/60 text-left text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-5 py-2">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((r, i) => (
              <tr key={i} className="border-t border-white/60" data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-5 py-2 font-mono text-xs">
                    {c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length > 50 && (
              <tr><td colSpan={columns.length} className="px-5 py-3 text-center text-xs text-slate-500">
                Showing first 50 of {rows.length}. Export CSV for full list.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
